import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { Calendar, ArrowRight, Clock, MapPin, Stethoscope, Calendar as CalendarIcon, User, Bell, Calendar as CalendarFull, Check, CalendarCheck, CalendarDays } from "lucide-react";

export const AppointmentSection = () => {
  const isMobile = useIsMobile();
  const navigatePath = isMobile ? "/rendez-vous-mobile" : "/rendez-vous";
  
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center px-8 max-h-[470px] relative overflow-hidden">
      {/* Transparent background */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      {/* Texte à gauche, visuel/calendrier à droite */}
      <div className="space-y-6 order-1 md:order-1 relative z-10">
        <h2 className="text-3xl font-bold">Vos rendez-vous médicaux</h2>
        <p className="text-lg text-muted-foreground">
          Organisez et suivez tous vos rendez-vous médicaux en toute simplicité
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Planification et suivi de vos consultations</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Rappels automatiques personnalisés</span>
          </div>
        </div>
        <Button asChild>
          <Link to={navigatePath} className="flex items-center gap-2 group">
            Gérer mes rendez-vous
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>

      <div className="order-2 md:order-2 relative z-10">
        <div className="w-full h-[320px] bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl shadow-lg p-6 relative overflow-hidden">
          {/* Calendar interface */}
          <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white p-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CalendarFull className="h-5 w-5" />
              <h3 className="font-semibold">Rendez-vous</h3>
            </div>
            <div className="flex gap-2">
              <div className="bg-white/20 rounded-full p-1.5">
                <Bell className="h-4 w-4" />
              </div>
              <div className="bg-white/20 rounded-full p-1.5">
                <User className="h-4 w-4" />
              </div>
            </div>
          </div>
          
          {/* Mini calendar */}
          <div className="absolute top-20 left-4 bg-white rounded-lg shadow-md p-3 w-40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium">Mai 2024</span>
              <div className="flex gap-1">
                <button className="text-gray-400 hover:text-gray-700">◀</button>
                <button className="text-gray-400 hover:text-gray-700">▶</button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(day => (
                <span key={day} className="text-xs text-gray-500">{day}</span>
              ))}
              {Array.from({ length: 31 }, (_, i) => (
                <div 
                  key={(i + 1).toString()}
                  className={`text-xs p-1 rounded-full ${i === 14 ? 'bg-blue-500 text-white' : i === 22 ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : 'hover:bg-gray-100'}`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          </div>
          
          {/* Appointment cards */}
          <div className="absolute top-20 right-4 w-[calc(100%-11rem)] space-y-3">
            <div className="bg-white rounded-lg shadow-sm p-3 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-2 text-blue-600">
                  <Stethoscope className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium">Dr. Martin</h4>
                  <p className="text-xs text-gray-500">General consultation</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>14:30</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarIcon className="h-3 w-3" />
                      <span>15 Mai</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-100 text-green-800 rounded-full px-2 py-0.5 text-xs font-medium">
                  Confirmé
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-3 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-2 text-purple-600">
                  <User className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium">Dr. Dubois</h4>
                  <p className="text-xs text-gray-500">Therapeutic follow-up</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>11:00</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarIcon className="h-3 w-3" />
                      <span>23 Mai</span>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 text-xs font-medium">
                  To be confirmed
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom action buttons */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            <div className="bg-white rounded-full shadow px-3 py-1.5 flex items-center gap-1 text-xs font-medium text-blue-600 cursor-pointer hover:shadow-md transition-shadow">
              <CalendarCheck className="h-3.5 w-3.5" />
              <span>New Appointment</span>
            </div>
            <div className="bg-white rounded-full shadow px-3 py-1.5 flex items-center gap-1 text-xs font-medium text-blue-600 cursor-pointer hover:shadow-md transition-shadow">
              <CalendarDays className="h-3.5 w-3.5" />
              <span>All Appointments</span>
            </div>
          </div>
          
          {/* Animated elements */}
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-blue-200/50 animate-pulse" style={{animationDuration: "3s"}}></div>
          <div className="absolute top-1/3 right-1/3 w-10 h-10 rounded-full bg-green-200/40 animate-pulse" style={{animationDuration: "4s", animationDelay: "1s"}}></div>
          <div className="absolute bottom-1/4 right-10 w-6 h-6 rounded-full bg-purple-200/40 animate-pulse" style={{animationDuration: "3.5s", animationDelay: "0.5s"}}></div>
          
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 0.5;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.8;
                  transform: scale(1.1);
                }
              }
            `}
          </style>
        </div>
      </div>
    </div>
  );
};
